// Pure helpers for the Model Hub (no React, no network). The backend decides eligibility; the UI only presents it.

export const CATEGORY_LABELS = {
  video: "Video", image: "Gambar", music: "Musik", audio: "Audio", voice: "Suara & TTS", chat: "Chat AI",
};
export const CATEGORY_ORDER = ["video", "image", "music", "audio", "voice", "chat"];

// Honest availability copy for categories that have no usable model today. Shown instead of an empty list.
export const CATEGORY_NOTES = {
  image: "Model gambar belum tersedia. Menunggu adapter, harga ritel, dan aktivasi admin.",
  music: "Model musik belum tersedia. Menunggu adapter, harga ritel, dan aktivasi admin.",
  audio: "Model audio belum tersedia. Menunggu adapter, harga ritel, dan aktivasi admin.",
  voice: "Model suara dan TTS belum tersedia. Menunggu adapter, harga ritel, dan aktivasi admin.",
  chat: "Chat AI belum tersedia. Kontrak model dan metering biaya masih dalam proses.",
};

export const categoryLabel = (category) => CATEGORY_LABELS[category] || "Lainnya";

const text = (value) => (typeof value === "string" ? value : "");

// Only well-formed entries from the public list are kept; anything else is dropped, never repaired or guessed.
export const normalizeModels = (payload) => {
  const list = Array.isArray(payload?.models) ? payload.models : [];
  const seen = new Set();
  const out = [];
  for (const item of list) {
    const id = text(item?.model).trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({
      model: id, title: text(item.title) || id, provider: text(item.provider), category: text(item.category) || "unknown",
      description: text(item.description), retailPrice: item.retail_price && typeof item.retail_price === "object" ? item.retail_price : null,
    });
  }
  return out;
};

export const filterModels = (models, { category = "all", query = "" } = {}) => {
  const needle = query.trim().toLowerCase();
  return models.filter((m) => (category === "all" || m.category === category)
    && (!needle || [m.model, m.title, m.provider].some((v) => v.toLowerCase().includes(needle))));
};

// Groups in a fixed order; every known category appears (possibly empty) so "not available yet" is explicit.
export const groupByCategory = (models) => {
  const groups = CATEGORY_ORDER.map((category) => ({ category, label: categoryLabel(category), models: [] }));
  const index = new Map(groups.map((g) => [g.category, g]));
  for (const model of models) {
    if (!index.has(model.category)) {
      const extra = { category: model.category, label: categoryLabel(model.category), models: [] };
      index.set(model.category, extra);
      groups.push(extra);
    }
    index.get(model.category).models.push(model);
  }
  return groups;
};

// The hub never runs a model itself. A registry model is run by its application; anything else has no run action.
export const runAction = (detail) => {
  const via = detail?.run_via;
  if (via && typeof via.path === "string" && via.path.startsWith("/") && !via.path.startsWith("//")) {
    return { kind: "app", label: `Buka ${text(via.label) || "aplikasi"}`, to: via.path };
  }
  return { kind: "none", label: "Belum dapat dijalankan dari Model Hub", to: null };
};

export const estimateText = (detail) => {
  const estimate = detail?.estimate;
  if (!estimate || !Number.isInteger(estimate.credits) || estimate.credits <= 0) return "Estimasi biaya belum tersedia";
  const basis = estimate.basis || {};
  const parts = [basis.duration_seconds && `${basis.duration_seconds} detik`, basis.resolution, basis.aspect_ratio].filter(Boolean);
  return `${estimate.credits} kredit${parts.length ? ` · ${parts.join(" · ")}` : ""}`;
};

export const FIELD_KIND_LABELS = {
  text: "Teks", longtext: "Teks panjang", select: "Pilihan", integer: "Bilangan bulat", number: "Angka", boolean: "Ya/Tidak", tags: "Daftar teks",
};

// A detail request failed: map status to copy that never exposes why a model is blocked (blockers are admin-only).
export const detailErrorText = (error) => {
  const status = error?.response?.status;
  if (status === 404) return "Model tidak ditemukan.";
  if (status === 409) return "Model ini belum tersedia untuk digunakan.";
  if (status === 401) return "Sesi berakhir. Silakan masuk kembali.";
  return "Detail model tidak dapat dimuat saat ini.";
};
